import React, { useState, useEffect, useCallback } from "react"
import { Phone, Warning, Spinner } from "@phosphor-icons/react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog"


import { Button } from "../../ui/button"
import { Label } from "../../ui/label"
import { ContactSearch } from "../../ui/collection-search"
import { DivisionSearch } from "../../ui/collection-search"
import { useCallStore } from "../../../stores"
import { api } from "../../../lib/api/client"
import { enableDialing, dialNumber } from "../../../lib/twilio"
import { cn } from "../../../lib/utils"

interface ContactData {
  _id: string
  display_name?: string
  given_name?: string
  family_name?: string
  phone?: string
  phone_2?: string
  phone_3?: string
  city?: string
  state?: string
}

interface MatterData {
  _id: string
  name: string
  division: string
  current_step_name?: string
}

interface ContactInfoResponse {
  data: {
    contacts: ContactData[]
    matters: MatterData[]
  }
}

interface DialContactModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Pre-selected contact ID */
  contactId?: string
  /** Pre-selected matter ID */
  matterId?: string
  /** Pre-selected division ID (fallback if not derived from matter) */
  divisionId?: string
}

// Format phone for display
function formatPhone(phone: string): string {
  if (!phone) return ""
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

export function DialContactModal({
  open,
  onOpenChange,
  contactId: initialContactId,
  matterId: initialMatterId,
  divisionId: initialDivisionId,
}: DialContactModalProps) {
  const { browserDialing } = useCallStore()

  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingInfo, setIsFetchingInfo] = useState(false)

  // Contact tab state
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [contactInfo, setContactInfo] = useState<{
    contacts: ContactData[]
    matters: MatterData[]
  } | null>(null)
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null)
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null)
  const [selectedPhone, setSelectedPhone] = useState<string>("")
  const [selectedDivisionId, setSelectedDivisionId] = useState<string | null>(null)

  const deviceError = browserDialing === "error"

  const resetForm = useCallback(() => {
    setSelectedContactId(null)
    setContactInfo(null)
    setSelectedContact(null)
    setSelectedMatterId(null)
    setSelectedPhone("")
    setSelectedDivisionId(null)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onOpenChange(false)
  }, [resetForm, onOpenChange])

  // Fetch contact info when contact is selected
  const fetchContactInfo = useCallback(async (contactId: string) => {
    setIsFetchingInfo(true)
    setContactInfo(null)
    setSelectedContact(null)
    if (!initialMatterId) {
      setSelectedMatterId(null)
    }
    setSelectedPhone("")

    try {
      const response: ContactInfoResponse = await api.callCenter.contactInfo(contactId)

      if (response.data) {
        const data = {
          contacts: response.data.contacts || [],
          matters: response.data.matters || [],
        }
        setContactInfo(data)

        // Auto-select if single contact
        if (data.contacts.length === 1) {
          const contact = data.contacts[0]
          setSelectedContact(contact)
          if (contact.phone) {
            setSelectedPhone(contact.phone)
          }
        }

        // Auto-select if single matter (skip if matter already pre-set from props)
        if (data.matters.length === 1 && !initialMatterId) {
          setSelectedMatterId(data.matters[0]._id)
          setSelectedDivisionId(data.matters[0].division)
        }
      } else {
        toast.error("Failed to load contact information")
      }
    } catch (error) {
      console.error("Error fetching contact info:", error)
      toast.error("Failed to load contact information")
    } finally {
      setIsFetchingInfo(false)
    }
  }, [initialMatterId])

  // Fetch matter info when matter is selected (without contact)
  const fetchMatterInfo = useCallback(async (matterId: string) => {
    setIsFetchingInfo(true)
    setContactInfo(null)
    setSelectedContact(null)
    setSelectedPhone("")

    try {
      const response: ContactInfoResponse = await api.callCenter.matterInfo(matterId)

      if (response.data) {
        setContactInfo(response.data)

        // Auto-select the matter (it should be in the response)
        if (response.data.matters.length >= 1) {
          const matter = response.data.matters.find(m => m._id === matterId) || response.data.matters[0]
          setSelectedMatterId(matter._id)
          setSelectedDivisionId(matter.division)
        }

        // Auto-select if single contact
        if (response.data.contacts.length === 1) {
          const contact = response.data.contacts[0]
          setSelectedContact(contact)
          if (contact.phone) {
            setSelectedPhone(contact.phone)
          }
        }
      } else {
        toast.error("Failed to load matter information")
      }
    } catch (error) {
      console.error("Error fetching matter info:", error)
      toast.error("Failed to load matter information")
    } finally {
      setIsFetchingInfo(false)
    }
  }, [])

  // Handle contact selection change
  const handleContactSelect = useCallback(
    (option: { value: string; data: ContactData } | null) => {
      if (option) {
        setSelectedContactId(option.value)
        fetchContactInfo(option.value)
      } else {
        setSelectedContactId(null)
        setContactInfo(null)
        setSelectedContact(null)
        setSelectedMatterId(null)
        setSelectedPhone("")
      }
    },
    [fetchContactInfo]
  )

  // Handle internal contact selection (when multiple contacts from info)
  const handleInternalContactSelect = useCallback((contact: ContactData) => {
    setSelectedContact(contact)
    if (contact.phone) {
      setSelectedPhone(contact.phone)
    } else if (contact.phone_2) {
      setSelectedPhone(contact.phone_2)
    } else if (contact.phone_3) {
      setSelectedPhone(contact.phone_3)
    } else {
      setSelectedPhone("")
    }
  }, [])

  // Handle matter selection
  const handleMatterSelect = useCallback((matter: MatterData) => {
    setSelectedMatterId(matter._id)
    setSelectedDivisionId(matter.division)
  }, [])

  // Make the call
  const handleCall = useCallback(async () => {
    if (!selectedContact) {
      toast.info("Please select a contact")
      return
    }

    if (!selectedPhone) {
      toast.info("Please select a phone number")
      return
    }

    if (contactInfo?.matters.length && !selectedMatterId) {
      toast.info("Please select a matter")
      return
    }

    if (!selectedDivisionId) {
      toast.info("Please select a division")
      return
    }

    const address =
      selectedContact.city && selectedContact.state
        ? `${selectedContact.city}, ${selectedContact.state}`
        : ""

    const name =
      selectedContact.display_name ||
      `${selectedContact.given_name || ""} ${selectedContact.family_name || ""}`.trim() ||
      selectedPhone

    setIsLoading(true)
    try {
      await enableDialing()
      await dialNumber({
        name,
        address,
        number: selectedPhone,
        contact: selectedContact._id,
        division: selectedDivisionId || undefined,
        matter: selectedMatterId || undefined,
      })
      handleClose()
    } catch (error) {
      console.error("Error making call:", error)
      toast.error("Failed to make call")
    } finally {
      setIsLoading(false)
    }
  }, [
    selectedContact,
    selectedPhone,
    selectedMatterId,
    selectedDivisionId,
    contactInfo,
    handleClose,
  ])

  // Initialize with pre-selected values
  useEffect(() => {
    if (open) {
      if (initialContactId) {
        // Contact provided - fetch contact info (may include matters)
        setSelectedContactId(initialContactId)
        fetchContactInfo(initialContactId)
        if (initialMatterId) {
          setSelectedMatterId(initialMatterId)
        }
      } else if (initialMatterId) {
        // Only matter provided - fetch matter info (includes contacts on matter)
        setSelectedMatterId(initialMatterId)
        fetchMatterInfo(initialMatterId)
      }
    }
  }, [open, initialContactId, initialMatterId, fetchContactInfo, fetchMatterInfo])

  // When contactInfo loads and a matter is already pre-selected, set its division
  useEffect(() => {
    if (contactInfo?.matters.length && selectedMatterId && !selectedDivisionId) {
      const match = contactInfo.matters.find((m) => m._id === selectedMatterId)
      if (match?.division) {
        setSelectedDivisionId(match.division)
      }
    }
  }, [contactInfo, selectedMatterId, selectedDivisionId])

  // Set initial division from prop if provided and no division set yet
  useEffect(() => {
    if (open && initialDivisionId && !selectedDivisionId) {
      setSelectedDivisionId(initialDivisionId)
    }
  }, [open, initialDivisionId, selectedDivisionId])

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open, resetForm])

  const hasPhoneNumbers = selectedContact && (selectedContact.phone || selectedContact.phone_2 || selectedContact.phone_3)

  // Determine if we have context (prefilled contact or matter)
  const hasContext = !!(initialContactId || initialMatterId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            {deviceError ? "Device Error" : hasContext ? "Call A Number" : "Make a Call"}
          </DialogTitle>
        </DialogHeader>

        {deviceError ? (
          <div className="py-4">
            <div className="flex items-center gap-2 text-destructive">
              <Warning className="h-5 w-5" />
              <p className="text-sm font-medium">
                Please refresh your page to continue making calls.
              </p>
            </div>
          </div>
        ) : hasContext ? (
          /* Context Mode - Direct selection interface (no search/tabs) */
          <div className="space-y-4 py-2">
            {isFetchingInfo && (
              <div className="flex items-center justify-center py-8">
                <Spinner className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {contactInfo && !isFetchingInfo && (
              <>
                {/* Matter Selection */}
                {contactInfo.matters.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase text-muted-foreground">
                      Select the matter you are calling in reference to:
                    </Label>
                    <div className="rounded-md border bg-muted/30 p-3 space-y-2 max-h-[150px] overflow-y-auto">
                      {contactInfo.matters.map((matter) => (
                        <label
                          key={matter._id}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="matterSelect"
                            checked={selectedMatterId === matter._id}
                            onChange={() => handleMatterSelect(matter)}
                            className="h-4 w-4 text-primary accent-primary"
                          />
                          <span className="text-sm">
                            {matter.name}
                            {matter.current_step_name && (
                              <span className="text-muted-foreground">
                                {" "}
                                - {matter.current_step_name}
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Division Selection (fallback when no matters and no initial division) */}
                {contactInfo.matters.length === 0 && !initialDivisionId && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase text-muted-foreground">
                      Select Division
                    </Label>
                    <DivisionSearch
                      value={selectedDivisionId}
                      onChange={(opt) => setSelectedDivisionId(opt?.value || null)}
                      placeholder="Select a division..."
                    />
                  </div>
                )}

                {/* Contact Selection */}
                {contactInfo.contacts.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase text-muted-foreground">
                      Select the contact to call:
                    </Label>
                    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                      {contactInfo.contacts.map((contact) => (
                        <label
                          key={contact._id}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="contactSelect"
                            checked={selectedContact?._id === contact._id}
                            onChange={() => handleInternalContactSelect(contact)}
                            className="h-4 w-4 text-primary accent-primary"
                          />
                          <span className="text-sm flex items-center gap-1.5">
                            {contact.phone || contact.phone_2 || contact.phone_3 ? (
                              <Phone className="h-3.5 w-3.5 text-success" />
                            ) : (
                              <Warning className="h-3.5 w-3.5 text-warning" />
                            )}
                            {contact.display_name ||
                              `${contact.given_name || ""} ${contact.family_name || ""}`.trim() ||
                              contact.phone ||
                              "Unknown"}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Phone Number Selection */}
                {selectedContact && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase text-muted-foreground">
                      Select the number to call:
                    </Label>
                    {hasPhoneNumbers ? (
                      <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                        {selectedContact.phone && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="phoneSelect"
                              checked={selectedPhone === selectedContact.phone}
                              onChange={() => setSelectedPhone(selectedContact.phone!)}
                              className="h-4 w-4 text-primary accent-primary"
                            />
                            <span className="text-sm">
                              {formatPhone(selectedContact.phone)} - Main Number
                            </span>
                          </label>
                        )}
                        {selectedContact.phone_2 && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="phoneSelect"
                              checked={selectedPhone === selectedContact.phone_2}
                              onChange={() => setSelectedPhone(selectedContact.phone_2!)}
                              className="h-4 w-4 text-primary accent-primary"
                            />
                            <span className="text-sm">
                              {formatPhone(selectedContact.phone_2)} - Secondary
                            </span>
                          </label>
                        )}
                        {selectedContact.phone_3 && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="phoneSelect"
                              checked={selectedPhone === selectedContact.phone_3}
                              onChange={() => setSelectedPhone(selectedContact.phone_3!)}
                              className="h-4 w-4 text-primary accent-primary"
                            />
                            <span className="text-sm">
                              {formatPhone(selectedContact.phone_3)} - Other
                            </span>
                          </label>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 rounded-md border border-warning/30 bg-warning/10">
                        <Warning className="h-4 w-4 text-warning" />
                        <span className="text-sm text-warning">
                          This contact has no phone number on file.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* No Context Mode - Search for contact */
          <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Search Contact</Label>
                <ContactSearch
                  value={selectedContactId}
                  onChange={handleContactSelect}
                  placeholder="Type to search contacts..."
                />
              </div>

              {isFetchingInfo && (
                <div className="flex items-center justify-center py-4">
                  <Spinner className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}

              {contactInfo && !isFetchingInfo && (
                <div className="space-y-4">
                  {/* Matter Selection */}
                  {contactInfo.matters.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase text-muted-foreground">
                        Select Matter
                      </Label>
                      <div className="rounded-md border bg-muted/30 p-3 space-y-2 max-h-[150px] overflow-y-auto">
                        {contactInfo.matters.map((matter) => (
                          <label
                            key={matter._id}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="matterSelect"
                              checked={selectedMatterId === matter._id}
                              onChange={() => handleMatterSelect(matter)}
                              className="h-4 w-4 text-primary"
                            />
                            <span className="text-sm">
                              {matter.name}
                              {matter.current_step_name && (
                                <span className="text-muted-foreground">
                                  {" "}
                                  - {matter.current_step_name}
                                </span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Division Selection (fallback when no matters and no initial division) */}
                  {contactInfo.matters.length === 0 && !initialDivisionId && (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase text-muted-foreground">
                        Select Division
                      </Label>
                      <DivisionSearch
                        value={selectedDivisionId}
                        onChange={(opt) => setSelectedDivisionId(opt?.value || null)}
                        placeholder="Select a division..."
                      />
                    </div>
                  )}

                  {/* Contact Selection (when multiple) */}
                  {contactInfo.contacts.length > 1 && (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase text-muted-foreground">
                        Select Contact
                      </Label>
                      <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                        {contactInfo.contacts.map((contact) => (
                          <label
                            key={contact._id}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="contactSelect"
                              checked={selectedContact?._id === contact._id}
                              onChange={() => handleInternalContactSelect(contact)}
                              className="h-4 w-4 text-primary"
                            />
                            <span className="text-sm flex items-center gap-1.5">
                              {contact.phone || contact.phone_2 || contact.phone_3 ? (
                                <Phone className="h-3.5 w-3.5 text-success" />
                              ) : (
                                <Warning className="h-3.5 w-3.5 text-warning" />
                              )}
                              {contact.display_name ||
                                `${contact.given_name || ""} ${contact.family_name || ""}`.trim() ||
                                contact.phone ||
                                "Unknown"}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Phone Number Selection */}
                  {selectedContact && (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase text-muted-foreground">
                        Select Phone Number
                      </Label>
                      {hasPhoneNumbers ? (
                        <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                          {selectedContact.phone && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="phoneSelect"
                                checked={selectedPhone === selectedContact.phone}
                                onChange={() => setSelectedPhone(selectedContact.phone!)}
                                className="h-4 w-4 text-primary"
                              />
                              <span className="text-sm">
                                {formatPhone(selectedContact.phone)} - Main
                              </span>
                            </label>
                          )}
                          {selectedContact.phone_2 && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="phoneSelect"
                                checked={selectedPhone === selectedContact.phone_2}
                                onChange={() => setSelectedPhone(selectedContact.phone_2!)}
                                className="h-4 w-4 text-primary"
                              />
                              <span className="text-sm">
                                {formatPhone(selectedContact.phone_2)} - Secondary
                              </span>
                            </label>
                          )}
                          {selectedContact.phone_3 && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="phoneSelect"
                                checked={selectedPhone === selectedContact.phone_3}
                                onChange={() => setSelectedPhone(selectedContact.phone_3!)}
                                className="h-4 w-4 text-primary"
                              />
                              <span className="text-sm">
                                {formatPhone(selectedContact.phone_3)} - Other
                              </span>
                            </label>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-3 rounded-md border border-warning/30 bg-warning/10">
                          <Warning className="h-4 w-4 text-warning" />
                          <span className="text-sm text-warning">
                            This contact has no phone number on file.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Close
          </Button>
          {deviceError ? (
            <Button onClick={() => window.location.reload()}>Refresh Page</Button>
          ) : (
            <Button
              onClick={handleCall}
              disabled={isLoading}
              className={cn(hasContext && "bg-success hover:bg-success/90")}
            >
              {isLoading ? (
                <>
                  <Spinner className="h-4 w-4 animate-spin mr-2" />
                  Calling...
                </>
              ) : (
                <>
                  <Phone className="h-4 w-4 mr-2" />
                  {hasContext ? "Call Number" : "Call"}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
